import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Droplets, Minus, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = '/api';
const C = { text: '#1A1A18', muted: '#9A9590', accent: '#C67A5C', border: '#E8E3DB', blue: '#4A90D9' };

export function WaterLogModal({ open, onClose }) {
  const { authHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const goalMl = data?.contextual_target?.target_ml || user?.water_goal_ml || 2500;
  const target = Math.round(goalMl / 250);
  const cups = data ? Math.round(data.total_ml / 250) : 0;
  const pct = Math.min(100, Math.round((cups / target) * 100));

  const fetchWater = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/water/today`, authHeaders());
      setData(res.data);
    } catch {}
  }, [authHeaders]);

  useEffect(() => { if (open) fetchWater(); }, [open, fetchWater]);

  const addCup = async () => {
    try {
      const res = await axios.post(`${API}/water/log`, { amount_ml: 250, beverage: 'water' }, authHeaders());
      setData(res.data);
      window.dispatchEvent(new Event('water-logged'));
    } catch {}
  };

  const removeCup = async () => {
    if (!data || data.total_ml <= 0) return;
    try {
      const res = await axios.post(`${API}/water/log`, { amount_ml: -250 }, authHeaders());
      setData(res.data);
      window.dispatchEvent(new Event('water-logged'));
    } catch {}
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 20px' }}
      onClick={onClose}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)' }} />
      <div onClick={e => e.stopPropagation()} style={{
        position: 'relative', width: '100%', maxWidth: 420, background: '#FDFBF7',
        borderRadius: 24, padding: '24px 24px 32px', boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
      }}>
        <button data-testid="water-modal-close" onClick={onClose}
          style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer' }}>
          <X style={{ width: 20, height: 20, color: C.muted }} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <Droplets style={{ width: 20, height: 20, color: C.blue }} />
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: C.text, margin: 0 }}>
            Water Intake
          </h2>
        </div>

        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <p style={{ fontSize: 48, fontWeight: 700, color: C.blue, margin: 0, fontFamily: "'DM Sans', sans-serif" }}>
            {cups}
          </p>
          <p style={{ fontSize: 14, color: C.muted, margin: 0, fontFamily: "'DM Sans', sans-serif" }}>
            of {target} cups
          </p>
        </div>

        <div style={{ height: 8, borderRadius: 4, background: '#E8E3DB', marginBottom: 20, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 4,
            background: `linear-gradient(90deg, ${C.blue}80, ${C.blue})`,
            width: `${pct}%`, transition: 'width 0.3s ease',
          }} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24, marginBottom: 20 }}>
          <button data-testid="water-remove" onClick={removeCup}
            style={{
              width: 56, height: 56, borderRadius: 28, border: `2px solid ${C.border}`,
              background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
            <Minus style={{ width: 20, height: 20, color: C.text }} />
          </button>
          <button data-testid="water-add" onClick={addCup}
            style={{
              width: 56, height: 56, borderRadius: 28, border: 'none',
              background: C.blue, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
            <Plus style={{ width: 20, height: 20, color: '#fff' }} />
          </button>
        </div>

        <button data-testid="water-details-link"
          onClick={() => { onClose(); navigate('/hydration'); }}
          style={{
            width: '100%', padding: '12px', borderRadius: 12, border: `1px solid ${C.border}`,
            background: 'transparent', fontSize: 13, fontWeight: 500,
            color: C.muted, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
          }}>
          View full hydration details
        </button>
      </div>
    </div>,
    document.body
  );
}
